import { formatCalendarDate } from '@/lib/datetime/formatDateTime'

export const NEWS_NEW_WINDOW_DAYS = 7

const SENTINEL_NEWS_DATE_PREFIX = '0001-01-01'

const parseDate = (dateString?: string) => {
  if (!dateString) {
    return null
  }

  const date = new Date(dateString)
  return Number.isNaN(date.getTime()) ? null : date
}

export const isValidNewsDate = (dateString?: string) => {
  if (!dateString || dateString.startsWith(SENTINEL_NEWS_DATE_PREFIX)) {
    return false
  }

  return parseDate(dateString) !== null
}

export const getNewsDisplayDate = (news: {
  news_date?: string
  created_at?: string
}) => (isValidNewsDate(news.news_date) ? news.news_date : news.created_at)

// News dates are calendar dates (the publish day), so they are formatted in UTC
// and never shift across the viewer's timezone — unlike instants such as
// completed_at, a bare day must read the same everywhere (OCE-1226).
export const formatNewsDate = (dateString?: string, locale = 'en') =>
  formatCalendarDate(dateString, { locale, preset: 'newsDate', fallback: '' })

export const formatNewsShortDate = (dateString?: string, locale = 'en') =>
  formatCalendarDate(dateString, {
    locale,
    preset: 'dayMonthYearLong',
    fallback: ''
  })

export const formatLatestBlockDate = (dateString?: string, locale = 'en') =>
  formatCalendarDate(dateString, { locale, preset: 'date', fallback: '' })

export const getNewsReadingTimeMinutes = ({
  content,
  readingTimeSeconds
}: {
  content?: string
  readingTimeSeconds?: number
}) => {
  if (readingTimeSeconds && readingTimeSeconds > 0) {
    return Math.max(1, Math.ceil(readingTimeSeconds / 60))
  }

  const words = content?.trim().split(/\s+/).filter(Boolean).length || 0
  return words > 0 ? Math.max(1, Math.ceil(words / 200)) : undefined
}

export const getNewsUpdatedDate = ({
  modifiedAt,
  publishedAt
}: {
  modifiedAt?: string
  publishedAt?: string
}) => {
  const modified = parseDate(modifiedAt)
  const published = parseDate(publishedAt)

  if (!modified || !published || modified <= published) {
    return undefined
  }

  return modifiedAt
}

export const formatNewsRelativeTime = (
  dateString?: string,
  locale = 'en'
): string => {
  const date = parseDate(dateString)
  if (!date) {
    return ''
  }

  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) {
    return ''
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const diffMinutes = Math.floor(diffMs / (1000 * 60))

  if (diffMinutes < 60) {
    return rtf.format(-Math.max(diffMinutes, 1), 'minute')
  }

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour')
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) {
    return rtf.format(-diffDays, 'day')
  }

  return formatNewsShortDate(dateString, locale)
}

export const isNewsNew = (
  dateString?: string,
  windowDays: number = NEWS_NEW_WINDOW_DAYS
) => {
  const date = parseDate(dateString)
  if (!date) {
    return false
  }

  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  return diffDays <= windowDays
}
